import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink, Play } from "lucide-react";
import { LocalizedLink } from "./LocalizedLink";
import { useLanguage } from "../i18n/LanguageProvider";

/**
 * Project card. Each project carries an accent colour that tints the cover
 * wash, the status chip and the hover border, so a grid of projects reads as
 * distinct pieces of work rather than a repeated template.
 */
export const ProjectCard = ({ project, index = 0, featured = false }) => {
  const { t, pick } = useLanguage();
  const accent = project.accent || "#7C8CFF";

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: Math.min(index, 4) * 0.06 }}
      data-testid={`project-card-${project.id}`}
      className="group h-full"
      style={{ "--acc": accent }}
    >
      <LocalizedLink
        to={`/projects/${project.slug}`}
        className={`flex h-full flex-col overflow-hidden rounded-3xl border border-white/5 bg-[#0F0F10] transition-colors duration-300 hover:border-[color-mix(in_srgb,var(--acc)_45%,transparent)] ${
          featured ? "md:flex-row" : ""
        }`}
      >
        <div
          className={`relative overflow-hidden ${
            featured ? "aspect-[16/10] md:aspect-auto md:w-1/2" : "aspect-[16/10]"
          }`}
        >
          <picture>
            {project.imageWebp && <source type="image/webp" srcSet={project.imageWebp} />}
            <img
              src={project.image}
              alt={project.title}
              width={1200}
              height={750}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
          </picture>
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to top, #0F0F10 2%, transparent 42%),
                           radial-gradient(90% 70% at 20% 0%, color-mix(in srgb, ${accent} 35%, transparent) 0%, transparent 70%)`,
            }}
          />

          <div className="absolute left-5 top-5 flex flex-wrap gap-2">
            <span className="rounded-full border border-white/10 bg-black/60 px-3 py-1 text-xs font-medium text-zinc-200 backdrop-blur-md">
              {project.platform}
            </span>
            <span
              className="rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-md"
              style={{
                color: accent,
                borderColor: `color-mix(in srgb, ${accent} 35%, transparent)`,
                backgroundColor: "rgba(0,0,0,0.55)",
              }}
            >
              {pick(project, "status")}
            </span>
          </div>

          {project.liveAppPath && (
            <span className="absolute bottom-5 left-5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur-md">
              <Play size={11} strokeWidth={2} />
              {t("projects.liveOnSite")}
            </span>
          )}
        </div>

        <div className={`flex flex-1 flex-col p-7 ${featured ? "md:p-9" : ""}`}>
          <div className="mb-3 flex items-start justify-between gap-4">
            <h3
              className={`font-semibold tracking-tight text-white transition-colors duration-200 group-hover:text-[var(--acc)] ${
                featured ? "text-2xl md:text-3xl" : "text-xl"
              }`}
            >
              {project.title}
            </h3>
            <ArrowUpRight
              size={featured ? 22 : 18}
              strokeWidth={1.5}
              className="mt-1 shrink-0 text-zinc-700 transition-colors duration-200 group-hover:text-[var(--acc)]"
            />
          </div>

          <p className="text-sm leading-relaxed text-zinc-400">{pick(project, "tagline")}</p>

          {featured && project.metrics && (
            <dl className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {project.metrics.map((metric) => (
                <div key={metric.label}>
                  <dt className="text-xs text-zinc-600">{pick(metric, "label")}</dt>
                  <dd
                    className="mt-0.5 font-mono text-lg font-semibold tabular-nums"
                    style={{ color: accent }}
                  >
                    {metric.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-white/5 pt-5">
            {project.tags.slice(0, featured ? 5 : 3).map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-white/5 px-2.5 py-0.5 text-xs font-medium text-zinc-400"
              >
                {tag}
              </span>
            ))}
            <span className="ml-auto text-xs text-zinc-600">{project.year}</span>
          </div>
        </div>
      </LocalizedLink>
    </motion.article>
  );
};

export const ExternalProjectLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-200 hover:text-white"
  >
    {children}
    <ExternalLink size={14} strokeWidth={1.5} />
  </a>
);
